// Types
type CustomerStatus = {
	Icon: string
	Description: string
}

type ContactConsent = {
	Revoked: boolean
	DecidedAt: string
	Source: "agent" | "customer"
}

type EmailWithDetails = {
	Label: string
	Value: string
	Status: CustomerStatus
	ESIGNEligible: boolean
	RemovedAt?: string
	RemovedSource?: "agent" | "customer"
	Consent?: ContactConsent
}

type PhoneWithDetails = {
	Label: string
	Value: string
	Status: CustomerStatus
	RemovedAt?: string
	RemovedSource?: "agent" | "customer"
	Consent?: ContactConsent
}

type DigitalCommunicationConsentOptionItem = {
	ID: string
	Display: string
	Selected: boolean
}

type DigitalCommunicationConsentOption = {
	Label: string
	ID: string
	Items: DigitalCommunicationConsentOptionItem[]
}

type SettlementOfferResponse = {
	DateFormat: string
	DefaultEndDate: string
	MinEndDate: string
	Accounts: string[]
	DefaultDiscountPercentage: number
	MinPercentage: number
	MaxPercentage: number
	FullBalance: string
}

type Account = {
	ID: string
	ClientID: string
	AccountRef: string
	AccountNumber: string
	ClientAccountRef: string
	CreatedDate: string
	Status: AccountStatus
	OriginalCreditor: string
	OriginalCreditorAccountNumber: string
	CurrentBalance: string
	AccruedInterest: string
	Fee: string
	AvailableForSIF: boolean
	LastPaymentDate: string
	LastPaymentAmount: string
}

type PaymentPlanAccount = {
	ID: string
	AccountRef: string
	Balance: string
	SOLExpired: boolean
	Details: AccountDetail[]
	ItemizationBalanceBreakdown: AccountDetail[]
	// Absent for the clients with no custom-field form, which is nearly all of
	// them — the account view leaves the section out rather than showing it empty.
	CustomFields?: AccountDetail[]
	CustomFieldsRecordedAt?: string
	CustomFieldsFormVersion?: string
	ReferredAt: string
	Closed: boolean
}

type ClientBanner = {
	Content: string
}

type CollectionHold = {
	HoldID: string
	Reason: string
	ReasonDisplay: string
	Display: string
	ReferenceID?: string
	ReferenceType?: string
	ManuallyReleasable: boolean
}

type DisputeReason = {
	ID: string
	Reason: string
}

type AccountDetail = {
	Label: string
	Value: string
	Warn: string
}

type PaymentPlanSettlementOffer = {
	DiscountRate: string
	AccountBalance: string
	DiscountAmount: string
	OfferEndDate: string
	SettlementLimitDate: string
}

type OutstandingAccount = {
	ID: string
	AccountRef: string
	Balance: string
	SOLExpired: boolean
	DisputeReasons: DisputeReason[]
	Details: AccountDetail[]
	ItemizationBalanceBreakdown: AccountDetail[]
	// Absent for the clients with no custom-field form, which is nearly all of
	// them — the account view leaves the section out rather than showing it empty.
	CustomFields?: AccountDetail[]
	CustomFieldsRecordedAt?: string
	CustomFieldsFormVersion?: string
	ReferredAt: string
	PaymentPlanSettlementOffers: PaymentPlanSettlementOffer[]
	Closed: boolean
}

type AccountStatus =
	| "PaymentBeingProcessed"
	| "InPaymentPlan"
	| "Settled"
	| "Disputed"
	| "Recalled"
	| "CustomerCaseEscalated"
	| "PendingInitialBankruptcyScrub"
	| "BankruptcyScrubDetected"
	| "PendingInitialServiceMembersScrub"
	| "ServiceMembersScrubDetected"
	| "PendingInitialDeceasedScrub"
	| "DeceasedScrubDetected"
	| "NotificationOfAssignmentPending"
	| "NotificationOfAssignmentPendingDueToEmailScrub"
	| "NotificationOfAssignmentPermanentlyUndelivered"
	| "NotificationOfAssignmentTemporarilyUndelivered"
	| "ClosedDueToStatuteOfLimitations"
	| "Outstanding"
	| "Cleared"

type OtherAccount = {
	ID: string
	AccountRef: string
	Balance: string
	Status: AccountStatus
	SOLExpired: boolean
	Details: AccountDetail[]
	ItemizationBalanceBreakdown: AccountDetail[]
	// Absent for the clients with no custom-field form, which is nearly all of
	// them — the account view leaves the section out rather than showing it empty.
	CustomFields?: AccountDetail[]
	CustomFieldsRecordedAt?: string
	CustomFieldsFormVersion?: string
	ReferredAt: string
	Closed: boolean
}

type Transaction = {
	Amount: string
	Date: string
	Time: string
	Type: string
	Description: string
	PaymentArrangementID?: string
	CommitmentStatus?: "pending" | "cancelled" | "succeeded" | "failed"
	ScheduledDate?: string
}

type CommsOptOutDate = {
	Label: string
	Display: string
}

type EmailLeadWithStatus = {
	Email: string
	IsEnabled: boolean
}

type CommsOptionItem = {
	ID: string
	Display: string
}

type CommsOption = {
	Label: string
	ID: string
	Items: CommsOptionItem[]
}

type ContactInstruction = {
	Content: string
}

type EmailLeadRemovalReason = {
	ID: string
	Display: string
}

type Note = {
	[key: string]: any
}

// Client Cases Types
type ClientCasesClient = {
	ID: string
	Name: string
	CountryCode: string
}

type ClientListItem = {
	ID: string
	Name: string
	CountryCode: string
	// The custom fields this client has recorded, sorted. Absent for clients with
	// none, which is most of them — the advanced search form reads that as "hide
	// the custom-field section", so there is nothing to distinguish from empty.
	//
	// Carried on the client rather than fetched per client: the form already has
	// this list, so selecting a client populates the field picker with no request
	// at all.
	CustomFields?: string[]
}

type ClientsListResponse = ClientListItem[]

type ClientCasesCaseType = {
	ID: string
	Label: string
}

type ClientCasesSortOption = {
	ID: string
	Label: string
}

type ClientCasesOpenedByOption = {
	ID: string
	Label: string
}

type ClientCasesOpenedWithinOption = {
	ID: string
	Label: string
}

type ClientCase = {
	CaseID: string
	CaseType: string
	CustomerID: string
	CustomerRef: string
	CustomerName: string
	ClientID: string
	ClientName: string
	CountryCode: string
	// US state code of the customer's current address; empty outside the US or when unknown.
	CustomerState: string
	SubmittedOn: string
	SubmittedDaysAgo: number
	ExpiryDate: string | null
	ExpireInDays: number | null
	WaitParty?: string | null
	WaitUntil?: string | null
	ParkedAt?: string | null
}

type ClientMyCase = ClientCase & {
	LastAssignedAt: string
}

type ClientCasesExportType = {
	ID: string
	Label: string
}

type ClientCasesExportColumn = {
	ID: string
	Label: string
	Default?: boolean
}

type ClientCasesResponse = {
	Clients: ClientCasesClient[]
	CaseTypes: ClientCasesCaseType[]
	SortOptions: ClientCasesSortOption[]
	WaitParties: CaseWaitParty[]
	OpenedByOptions: ClientCasesOpenedByOption[]
	OpenedWithinOptions: ClientCasesOpenedWithinOption[]
	CountryOptions: string[]
	StateOptions: string[]
	MyCases: ClientMyCase[]
	Cases: ClientCase[]
	Total: number
	Capped: boolean
	CaseOwnershipEnabled: boolean
	// Export UI is fully backend-driven.
	ExportEnabled: boolean
	ExportTypes: ClientCasesExportType[]
	ExportColumns: ClientCasesExportColumn[]
	ExportAttachmentsEnabled: boolean
	MaxDateRange: number
}

type CasesExportRequest = {
	AgentEmail: string
	ExportType: string
	ClientID?: string
	CaseType?: string
	DateFrom: string
	DateTo: string
	Columns: string[]
	IncludeAttachments?: boolean
}

// The export is generated synchronously; the response carries the presigned URL.
type CasesExportSubmitResponse = {
	DownloadURL: string
	SkippedAttachments?: number
}

// Pick up assigns the oldest unassigned active case to the agent and returns
// enough to open it.
type CasesPickUpResponse = {
	CustomerID: string
	CaseID: string
}

// Case Management Types
type CaseType = {
	ID: string
	Label: string
}

type CaseResolutionReason = {
	ID: string
	Label: string
}

type CaseResolution = {
	ID: string
	Label: string
	Description?: string
	ConsequenceDescription?: string
	ConsequenceAlert?: boolean
	CallToAction?: string
	Reasons: CaseResolutionReason[]
	NoteForms: CaseNoteForm[]
}

type CaseNoteStatus = {
	ID: string
	Label: string
}

type CaseNoteArchiveStatus = {
	ID: string
	Label: string
}

type CaseNote = {
	ID: string
	Label: string
	CustomerID: string
	Form: {
		Type: string
		Version: number
		Fields: Array<{Name: string; Type: string; Content: string}>
	}
	Uploads: Array<{ID: string; FileName: string}>
	AgentName: string
	AgentEmail: string
	Status: CaseNoteStatus
	ArchiveStatuses: CaseNoteArchiveStatus[]
	CreatedAt: string
}

type CaseNoteForm = {
	Type: string
	Label: string
	Version: number
	Fields: Array<{Name: string; Type: string; Required: boolean; Options?: string[]}>
	IsDefault?: boolean
}

type CaseAccountDetail = {
	Label: string
	Value: string
}

type ActiveCaseAccount = {
	AccountID: string
	AccountRef: string
	Balance: string
	// Optional while lambdas that predate the fields still answer.
	Status?: AccountStatus
	OriginatingCreditor?: CaseAccountDetail[]
	SOL?: CaseAccountDetail[]
	SOLExpired?: boolean
	Resolution: string | null
	IsTerminal: boolean
	Reason: string | null
	ResolverType: string | null
	ResolverName: string | null
	ResolverEmail: string | null
	ResolutionAt: string | null
}

type CaseWaitPartyEscalation = {
	OpensTicket: boolean
	Reason?: string
	Queue?: string
}

type CaseWaitParty = {
	ID: string
	Label: string
	HasDeadline: boolean
	// Only the client party is ever assessed, and only when a verdict could be
	// read; absent leaves the party escalatable rather than blocked.
	Escalation?: CaseWaitPartyEscalation | null
}

type CaseReturnReason = {
	ID: string
	Label: string
}

type CaseManual = {
	ID: string
	Name: string
	Text: string
}

type CaseJiraTicket = {
	Key: string
	URL: string
}

type ActiveCase = {
	CaseID: string
	CaseType: string
	Resolutions: CaseResolution[]
	ExpiryDate: string
	AssigneeEmail?: string | null
	LastAssignedAt?: string | null
	WaitParty?: string | null
	WaitUntil?: string | null
	ParkedAt?: string | null
	ReturnReason?: CaseReturnReason | null
	ReturnedAt?: string | null
	// Prospective, not recorded: the deadline a customer wait started now would
	// run to. Present whether or not the case is parked.
	WaitPeriodDays: number
	WaitParties: CaseWaitParty[]
	SubmitterType: string
	SubmitterName: string
	SubmitterEmail: string
	SubmittedAt: string
	Notes: string[]
	AttachNoteForms: CaseNoteForm[]
	Accounts: ActiveCaseAccount[]
	Manuals?: CaseManual[]
	JiraTicket?: CaseJiraTicket | null
}

type ClosedCase = {
	CaseID: string
	CaseType: string
	Resolution: string | null
	IsTerminal: boolean
	Reason: string | null
	ExpiryDate: string
	SubmitterType: string
	SubmitterName: string
	SubmitterEmail: string
	ResolverType: string
	ResolverName: string
	ResolverEmail: string
	SubmittedAt: string
	ClosedAt: string
	Notes: string[]
}

type ResolvedCaseAccount = {
	AccountID: string
	AccountRef: string
	Balance: string
	ClosedStatus: string | null
	Resolution: string
	IsTerminal: boolean
	Reason: string | null
	ResolverType: string
	ResolverName: string
	ResolverEmail: string
	ResolutionAt: string
}

type ResolvedAccountCase = {
	CaseID: string
	CaseType: string
	ExpiryDate: string
	SubmitterType: string
	SubmitterName: string
	SubmitterEmail: string
	SubmittedAt: string
	ClosedAt: string
	Notes: string[]
	Accounts: ResolvedCaseAccount[]
}

type ResolvedCases = {
	CustomerCases: ClosedCase[]
	AccountCases: ResolvedAccountCase[]
}

type CustomerAccount = {
	AccountID: string
	AccountRef: string
	Status?: AccountStatus
	OriginatingCreditor?: CaseAccountDetail[]
	SOL?: CaseAccountDetail[]
	SOLExpired?: boolean
}

type CasesResponse = {
	// Optional while lambdas that predate the fields still answer.
	CustomerRef?: string
	DOB?: string
	TotalOutstanding?: string
	CustomerAccounts?: CustomerAccount[]
	CaseTypes: CaseType[]
	ActiveCases: ActiveCase[]
	ClosedCases: ClosedCase[]
	ResolvedCases: ResolvedCases
	CaseOwnershipEnabled: boolean
}

type CaseConversationSummary = {
	id: string
	channel: string
	subject: string
	status: string
	lastActivityAt: string
	otherCaseIds: string[]
}

type ConversationSummary = {
	id: string
	channel: string
	subject: string
	status: string
	lastActivityAt: string
	caseIds: string[]
}

type ConversationMessageAttachment = {
	index: number
	filename: string
	mediaType: string
	sizeBytes: number
}

type ConversationMessage = {
	providerMessageId: string
	direction: string
	authoredBy?: string
	edited: boolean
	inReplyToProviderMessageId?: string
	subject: string
	occurredAt: string
	classificationCategory: string
	escalationReason: string
	deliveryStatus?: string
	deliveryReason?: string
	deliveryOccurredAt?: string
	body: string
	quoted: string
	attachments?: ConversationMessageAttachment[]
}

type ConversationTimeline = {
	id: string
	channel: string
	subject: string
	status: string
	fromAddress: string
	fromLabel: string
	toAddress: string
	toLabel: string
	mailbox: string
	createdAt: string
	lastActivityAt: string
	messages: ConversationMessage[]
	replyAnchorProviderMessageId: string
	hasMore: boolean
	caseOwnershipEnabled: boolean
	linkedCases: {caseId: string; assigneeEmail: string | null}[]
}

type DraftReplyRequest = {
	guidance: string
}

type EmailRecipient = {
	label: string
	email: string
}

type DraftReplyPromptMessage = {
	role: string
	content: string
}

type DraftReplyDraft = {
	subject: string
	bodyHtml: string
	bodyText: string
	footerText: string
	from: EmailRecipient
	replyTo: EmailRecipient
	to: EmailRecipient
	inReplyTo: string
	references: string[]
	rfc822MessageId: string
	modelVersion: string
}

type DraftReplyOk = {
	status: "ok"
	promptMessages: DraftReplyPromptMessage[]
	draft: DraftReplyDraft
	omittedFields?: string[]
	contactWarning?: string
	complianceFallback?: boolean
}

type DraftReplyBlocked = {
	status: "blocked"
	reason: "prompt_injection" | "conduct_exhausted"
}

type DraftReplyResponse = DraftReplyOk | DraftReplyBlocked

// The SMS route still lets the agent pick facts and the reply language; the
// email route always offers every fact in the customer's language.
type SMSDraftReplyRequest = {
	guidance: string
	fields: string[]
	replyInCustomerLanguage: boolean
}

// The numbers stay off the draft: the send route derives them from the same
// stored inbound source rather than trusting anything the client returns.
type SMSDraftReplyDraft = {
	text: string
	modelVersion: string
}

type SMSDraftReplyOk = {
	status: "ok"
	promptMessages: DraftReplyPromptMessage[]
	draft: SMSDraftReplyDraft
	omittedFields?: string[]
	contactWarning?: string
}

type SMSDraftReplyBlocked = {
	status: "blocked"
	reason: "prompt_injection" | "conduct_exhausted"
}

type SMSDraftReplyResponse = SMSDraftReplyOk | SMSDraftReplyBlocked

// No numbers on the way out either: the route replies on the number pair from
// the captured inbound source, and never trusts the client for them.
type SendSMSReplyRequest = {
	text: string
	guidance: string
	authoredBy: string
	modelVersion: string
}

type SendSMSReplyResponse = {
	status: "accepted"
}

type SendReplyRequest = {
	bodyHtml: string
	bodyText: string
	edited: boolean
	guidance: string
	authoredBy: string
	modelVersion: string
	recipient?: string
	attachment?: {noteId: string; uploadId: string}
}

type SendReplyAccepted = {
	status: "accepted"
	replyId: string
}

type SendReplyResponse = SendReplyAccepted

type ExpiryDuration = {
	ID: string
	Display: string
}

type ScaffoldCaseAccount = {
	AccountID: string
	AccountRef: string
	Balance: string
	Status: string
}

type ScaffoldCaseResponse = {
	CaseID: string
	CaseType: string
	CaseTypeLabel: string
	CaseTypeDescription?: string
	NoteForms: Array<{
		Type: string
		Label: string
		Version: number
		Fields: Array<{Name: string; Type: string; Required: boolean; Options?: string[]}>
		IsDefault?: boolean
	}>
	ExpiryConfig: {
		DefaultExpiryDuration: ExpiryDuration
		AllowSpecificExpiryDate: boolean
		AllowedDurations: ExpiryDuration[]
	} | null
	ActiveCaseIDs: string[]
	Accounts: ScaffoldCaseAccount[]
}

type LatitudeInfo = {
	Link?: string
	CollectionCommsEnabled?: boolean
	PaymentsEnabled?: boolean
}

type ActivePaymentPlan = {
	ID: string
	Frequency: string
	Amount: string
	InstalmentAmount: string
	StartDate: string
	EndDate: string
	PaymentMethod: string
	NextUpcomingInstalmentDate: string
	Offers: any[]
	ManagePlanURL: string
}

type CustomerCreatedActivityPayload = {
	Name: string
	DOB: string
	Emails: string[]
	Phones: string[]
	Address: string
}

type CustomerLatitudeReferredActivityPayload = {
	Name: string
	LatitudeID: string
	CustomerRef: string
	DOB: string
	Emails: string[]
	Phones: string[]
	Address: string
	IdentificationDocument: string
}

type CustomerLatitudeConvertedActivityPayload = Record<string, never>

type CustomerAddressChangedActivityPayload = {
	NewAddress: string
}

type CustomerAddressChangedByCustomerActivityPayload = {
	NewAddress: string
	AgentEmail: string
}

type CustomerCaseEscalatedActivityPayload = {
	CaseID?: string
}

type CustomerCaseSubmittedActivityPayload = {
	CaseID: string
	CaseType: string
	SubmitterType: string
	SubmitterEmail: string
	SubmitterName: string
}

type CustomerCaseResolvedActivityPayload = {
	CaseID: string
	CaseType: string
	Resolution: string
	Reason?: string
	ResolverType: string
	ResolverEmail: string
	ResolverName: string
}

type CustomerCaseAccountResolvedActivityPayload = {
	CaseID: string
	AccountID: string
	CaseType: string
	Resolution: string
	Reason?: string
	ResolverType: string
	ResolverEmail: string
	ResolverName: string
}

type CustomerCommunicationSnoozedActivityPayload = {
	AgentEmail: string
	EndDate: string
}

type CustomerCustomField = {
	Name: string
	Value: string
}

type CustomerCustomFieldsRecordedActivityPayload = {
	Fields: CustomerCustomField[]
}

type CustomerDigitalCommunicationConsentActivityPayload = Record<string, never>

type CustomerEmailCommunicationOptedActivityPayload = Record<string, never>

type CustomerEmailLeadRemovedActivityPayload = {
	EmailLead: string
	AgentEmail: string
	Reason: string
}

type CustomerEmailLeadsUpdateActivityPayload = {
	Emails: string[]
}

type CustomerEmailSentActivityPayload = {
	ContentURI: string
	Summary: string
	Recipient: string
}

type CustomerEmailUpdateActivityPayload = {
	Email: string
}

type CustomerEmailUpdateByCustomerActivityPayload = {
	Email: string
	AgentEmail: string
}

type CustomerESIGNConsentRequestedPayload = {
	AgentEmail: string
	To: string
	Subject: string
}

type CustomerESIGNConsentRevokedByAgentPayload = {
	RevokedAt: string
	RevokedVia: string
	AgentEmail: string
}

type CustomerInformationUpdatedActivityPayload = {
	Name: string
	DOB: string
}

type CustomerInformationUpdatedByCustomerActivityPayload = {
	Name: string
	DOB: string
	AgentEmail: string
}

type CustomerInstalmentOverrideAppliedActivityPayload = {
	WeeklyMaxDurationOverride: number
	PaymentAmount: number
	AppliedAtBalance: number
	Currency: string
	AgentEmail: string
}

type CustomerInstalmentOverrideResetActivityPayload = {
	WeeklyMaxDuration: number
	PaymentAmount: number
	ResetAtBalance: number
	Currency: string
	AgentEmail: string
}

type CustomerNOALetterSentActivityPayload = {
	ContentURI: string
	ContentDownloadURI: string
}

type CustomerPaymentArrangementCancelled = {
	PaymentArrangementID: string
	Reason: string
}

type CustomerPaymentArrangementCompleted = {
	PaymentArrangementID: string
}

type CustomerPaymentArrangementSetupSucceeded = {
	PaymentArrangementID: string
	StartDate: string
	EndDate: string
	InstalmentAmount: string
	NumberOfInstalments: number
	Frequency: string
}

type CustomerPaymentPlanCancelled = {
	PaymentPlanID: string
	Reason: string
}

type CustomerPaymentPlanCompleted = {
	PaymentPlanID: string
}

type CustomerPaymentPlanSetupSucceeded = {
	PaymentPlanID: string
	Amount: string
	StartDate: string
	EndDate: string
	InstalmentAmount: string
	NumberOfInstalments: number
	Frequency: string
}

type CustomerPhoneIdentityMatchActivityPayload = {
	Phone: string
}

type CustomerPhoneReassignedNumberActivityPayload = {
	Phone: string
}

type CustomerPhoneOptedOutForSMSActivityPayload = {
	Phone: string
}

type CustomerPhoneUpdateActivityPayload = {
	Phone: string
}

type CustomerPhoneUpdateByCustomerActivityPayload = {
	Phone: string
	AgentEmail: string
}

type CustomerPreferredLanguageConfiguredActivityPayload = Record<string, never>

type CustomerSMSActivityPayload = {
	SMSID: string
	ToNumber: string
	Text: string
	DeliveryAt: string
	SentAt: string
	FailedAt: string
	Type: string
	InitiatedAt: string
	FailedReason: string
}

type CustomerActivityBase = {
	Title: string
	HappenedAt: string
}

type CustomerActivityTypePayload =
	| {Type: "CustomerCreated"; Payload: CustomerCreatedActivityPayload}
	| {Type: "CustomerLatitudeReferred"; Payload: CustomerLatitudeReferredActivityPayload}
	| {Type: "CustomerLatitudeConverted"; Payload: CustomerLatitudeConvertedActivityPayload}
	| {
			Type: "CustomerAddressChanged"
			Payload: CustomerAddressChangedActivityPayload | CustomerAddressChangedByCustomerActivityPayload
	  }
	| {Type: "CustomerCaseEscalated"; Payload: CustomerCaseEscalatedActivityPayload}
	| {Type: "CustomerCaseSubmitted"; Payload: CustomerCaseSubmittedActivityPayload}
	| {Type: "CustomerCaseResolved"; Payload: CustomerCaseResolvedActivityPayload}
	| {Type: "CustomerCaseAccountResolved"; Payload: CustomerCaseAccountResolvedActivityPayload}
	| {Type: "CustomerCommunicationSnoozed"; Payload: CustomerCommunicationSnoozedActivityPayload}
	| {Type: "CustomerCustomFieldsRecorded"; Payload: CustomerCustomFieldsRecordedActivityPayload}
	| {Type: "CustomerDigitalCommunicationConsentGranted"; Payload: CustomerDigitalCommunicationConsentActivityPayload}
	| {Type: "CustomerDigitalCommunicationConsentDenied"; Payload: CustomerDigitalCommunicationConsentActivityPayload}
	| {
			Type: "CustomerEmailAdded"
			Payload: CustomerEmailUpdateActivityPayload | CustomerEmailUpdateByCustomerActivityPayload
	  }
	| {Type: "CustomerEmailLeadsAddedByDataWashing"; Payload: CustomerEmailLeadsUpdateActivityPayload}
	| {
			Type: "CustomerEmailRemoved"
			Payload: CustomerEmailUpdateActivityPayload | CustomerEmailUpdateByCustomerActivityPayload
	  }
	| {Type: "CustomerEmailLeadRemovedByCustomer"; Payload: CustomerEmailLeadRemovedActivityPayload}
	| {Type: "CustomerEmailLeadRemovedByThirdParty"; Payload: CustomerEmailLeadRemovedActivityPayload}
	| {
			Type: "CustomerPhoneAdded"
			Payload: CustomerPhoneUpdateActivityPayload | CustomerPhoneUpdateByCustomerActivityPayload
	  }
	| {
			Type: "CustomerPhoneRemoved"
			Payload: CustomerPhoneUpdateActivityPayload | CustomerPhoneUpdateByCustomerActivityPayload
	  }
	| {Type: "CustomerPhoneIdentityMatchedByScrub"; Payload: CustomerPhoneIdentityMatchActivityPayload}
	| {Type: "CustomerPhoneIdentityNotMatchedByScrub"; Payload: CustomerPhoneIdentityMatchActivityPayload}
	| {Type: "CustomerEmailCommunicationsOptedIn"; Payload: CustomerEmailCommunicationOptedActivityPayload}
	| {Type: "CustomerEmailCommunicationsOptedOut"; Payload: CustomerEmailCommunicationOptedActivityPayload}
	| {
			Type: "CustomerInformationUpdated"
			Payload: CustomerInformationUpdatedActivityPayload | CustomerInformationUpdatedByCustomerActivityPayload
	  }
	| {Type: "CustomerPreferredLanguageConfigured"; Payload: CustomerPreferredLanguageConfiguredActivityPayload}
	| {Type: "ESIGNConsentGranted"; Payload: CustomerDigitalCommunicationConsentActivityPayload}
	| {Type: "ESIGNConsentRequested"; Payload: CustomerESIGNConsentRequestedPayload}
	| {Type: "ESIGNConsentRevoked"; Payload: CustomerESIGNConsentRevokedByAgentPayload}
	| {Type: "CustomerPhoneHasBeenReassigned"; Payload: CustomerPhoneReassignedNumberActivityPayload}
	| {Type: "CustomerPhoneHasNotBeenReassigned"; Payload: CustomerPhoneReassignedNumberActivityPayload}
	| {Type: "CustomerPhoneOptedOutForSMS"; Payload: CustomerPhoneOptedOutForSMSActivityPayload}
	| {Type: "CustomerInstalmentOverrideApplied"; Payload: CustomerInstalmentOverrideAppliedActivityPayload}
	| {Type: "CustomerInstalmentOverrideReset"; Payload: CustomerInstalmentOverrideResetActivityPayload}
	| {Type: "SMSSent"; Payload: CustomerSMSActivityPayload}
	| {Type: "SMSFailed"; Payload: CustomerSMSActivityPayload}
	| {Type: "WelcomeSMSSent"; Payload: CustomerSMSActivityPayload}
	| {Type: "WelcomeSMSFailed"; Payload: CustomerSMSActivityPayload}
	| {Type: "HelpSMSSent"; Payload: CustomerSMSActivityPayload}
	| {Type: "HelpSMSFailed"; Payload: CustomerSMSActivityPayload}
	| {Type: "EmailSent"; Payload: CustomerEmailSentActivityPayload}
	| {Type: "NOASent"; Payload: CustomerEmailSentActivityPayload}
	| {Type: "NOALetterSent"; Payload: CustomerNOALetterSentActivityPayload}
	| {Type: "PaymentPlanSetupSucceeded"; Payload: CustomerPaymentPlanSetupSucceeded}
	| {Type: "PaymentPlanCompleted"; Payload: CustomerPaymentPlanCompleted}
	| {Type: "PaymentPlanCancelled"; Payload: CustomerPaymentPlanCancelled}
	| {Type: "PaymentArrangementSetupSucceeded"; Payload: CustomerPaymentArrangementSetupSucceeded}
	| {Type: "PaymentArrangementCompleted"; Payload: CustomerPaymentArrangementCompleted}
	| {Type: "PaymentArrangementCancelled"; Payload: CustomerPaymentArrangementCancelled}

type CustomerActivity = CustomerActivityBase & CustomerActivityTypePayload

type AccountActivityPayload = {
	ContentURI?: string
	Summary?: string
	Recipient?: string
	PaymentArrangementID?: string
	Reason?: string
	StartDate?: string
	EndDate?: string
	InstalmentAmount?: string
	NumberOfInstalments?: number
	Frequency?: string
	Name?: string
	DOB?: string
	Emails?: string[]
	Phones?: string[]
	Address?: string
	[key: string]: any
}

type AccountActivityField = {
	Label: string
	Value: string
}

type AccountActivity = {
	Fields: AccountActivityField[]
	HappenedAt: string
	IconType: string
	Title: string
	Type: string
}

type CustomerActivitiesResponse = {
	Activities: CustomerActivity[]
}

type AccountActivitiesResponse = {
	Activities: AccountActivity[]
}

type ActiveInstalmentCap = {
	MaxPaymentsWeekly: number
	MinPaymentAmount: number
}

type InstalmentCapHistoryEntry = {
	Type: string
	MaxPaymentsWeekly: number
	MinPaymentAmount: number
	Balance: number
	PerformedAt: string
	AgentEmail: string
}

type InstalmentCapsResponse = {
	ActiveInstalmentCap: ActiveInstalmentCap
	DefaultInstalmentCap: ActiveInstalmentCap
	InstalmentCaps: InstalmentCapHistoryEntry[]
	MaximumExtendableDuration: number
}

type Customer = {
	ID: string
	ClientID: string
	ClientName: string
	ClientDisplayName: string
	CustomerRef: string
	DOB: string
	FullName: string
	Address: string
	TotalCustomerBalanceInCents: number
	TotalCustomerAvailableBalanceInCents: number
	IdentificationDocument: string
	CountryForAddressUpdate: string
	CommunicationSnoozed: boolean
	SnoozedUntil: string
	OutstandingAccounts: OutstandingAccount[]
	PaymentPlanAccounts: PaymentPlanAccount[]
	Transactions: Transaction[]
	OtherAccounts: OtherAccount[]
	TotalOutstanding: string
	TotalInPaymentPlan: string
	TotalOther: string
	Emails: string[]
	EmailLeads: string[] | null
	EmailLeadsWithStatus: EmailLeadWithStatus[]
	EmailLeadRemovalReasons: EmailLeadRemovalReason[]
	PreferredLanguage: string
	Escalation: string
	ActivePaymentPlans: ActivePaymentPlan[]
	CommsOptOutDates: CommsOptOutDate[]
	CommsOptions: CommsOption[]
	LocalTime: string
	Notes: Note[]
	DigitalCommunicationConsentOptions: DigitalCommunicationConsentOption[]
	AllowGeneratePaymentLink: boolean
	AllAccountsBalanceBreakdown: AccountDetail[]
	WrittenStatuteOfLimitationsDisclosures: string
	VerbalStatuteOfLimitationsDisclosures: string
	NextCompliantTimeRangeInViewerLocalTime: string
	ContactFrequencyRules: string[]
	ContactInstructions: ContactInstruction[]
	EmailsWithDetails: EmailWithDetails[]
	PhonesWithDetails: PhoneWithDetails[]
	EnableCreateSettlementOffer: boolean
	ESIGNConsentRequired: boolean
	ESIGNConsentRequested: boolean
	ESIGNConsentRequestedAt: string | null
	ESIGNConsentGrantedAt: string | null
	CustomerPaidCommissions: boolean
	SystemOfRecord: string
	LatitudeLink: string
	Latitude: LatitudeInfo | null
	ClientBanners: ClientBanner[]
	EnableRocketOffersTab: boolean
	EnableCustomerOffersV2: boolean
	UseNotesV2: boolean
	EnableRocketCasesTab: boolean
	EnableRocketCollectionHolds: boolean
	CollectionHolds: CollectionHold[] | null
}

type HistoricMatch = {
	field: "phone" | "email"
	value: string
	removedAt: string
	removedSource: "agent" | "customer" | "third_party"
}

type CustomerSearchResult = {
	ID: string
	FullName: string
	DOB: string
	ClientName: string
	SystemOfRecord: string
	CollectionHold: boolean
	Emails?: string[]
	OtherAccountIDs?: Record<string, string>
	OutstandingAccountIDs?: Record<string, string>
	historicMatch?: HistoricMatch
	// Names the custom fields the searched value was found in. Returned only for a
	// value-only custom-field search — when the agent named a field they already
	// know the answer, so the backend leaves it off.
	matchedCustomFields?: string[]
}

type CustomerSearchResponse = CustomerSearchResult[]

type AdvancedSearchParams = {
	clientID?: string
	ssnLast4?: string
	firstName?: string
	middleName?: string
	lastName?: string
	phone?: string
	email?: string
	addressLine1?: string
	postcode?: string
	// Accepted by the backend search endpoint (a valid narrowing field) and serialized
	// by buildAdvancedSearchQuery, but intentionally not surfaced in AdvancedSearchPanel
	// yet — kept here so this type matches the backend contract.
	customerAccountRef?: string
	dob?: string
	// customFieldValue searches the client's custom fields. Requires clientID:
	// field names are per client and values collide across them. customFieldName
	// narrows to one field; without it the value is matched against every
	// searchable field, and the result names which ones it hit.
	customFieldValue?: string
	customFieldName?: string
	includeHistoricContactDetails?: boolean
	limit?: number
}

type OfferDetails = {
	ExpireDate: string
	Balance: string
	DiscountPercentage: string
	DiscountedAmount: string
	AmountToPay: string
	SettlementLimitDate?: string
}

type OfferData = {
	InFull?: OfferDetails
	PaymentPlan?: OfferDetails
}

type CustomerOfferTier = {
	TierID: string
	TierType: string
	MinimumNumberOfPayments?: number
	MaximumNumberOfPayments: number
	DiscountRate: number
	MinimumPaymentAmount: number
	Currency: string
	TimeFrameWeeks: number
}

type CustomerOffer = {
	OfferID: string
	StartDate: string
	EndDate: string
	Tiers: CustomerOfferTier[]
	CampaignID?: string
	AgentEmail?: string
	Notes?: string
	CreatedAt: string
	Status: "ACTIVE" | "REVOKED" | "EXPIRED"
}

type RevocationReason = {
	Value: string
	Label: string
}

type CustomerOffersResponse = {
	ActiveOffers: CustomerOffer[]
	InactiveOffers: CustomerOffer[]
	RevocationReasons: RevocationReason[]
}

type DevToolsStatementOfWork = {
	id: string
	productType: string
	payoutType: string
}

type DevToolsClient = {
	id: string
	name: string
	country: string
	statementsOfWork: DevToolsStatementOfWork[]
}

type DevToolsAccountInput = {
	balance: number
	statementOfWorkID: string
}

type DevToolsCustomerInput = {
	fullName: string
	dob: string
	email: string
	phone: string
}

type DevToolsCreateCustomerRequest = {
	clientID: string
	customer: DevToolsCustomerInput
	accounts: DevToolsAccountInput[]
	expediteProcesses: boolean
	simulateScrubs: boolean
}

type DevToolsCreateCustomerResponse = {
	customerID: string
	secureCode: string
	paymentPortalURL: string
}

type UnidentifiedInboxMessage = {
	ConversationID: string
	ProviderMessageID: string
	Mailbox: string
	FromAddress: string
	FromLabel: string
	Subject: string
	DeferralReason: string
	ReceivedAt: string
	DeferredAt: string
}

type UnidentifiedInboxResponse = {
	Messages: UnidentifiedInboxMessage[]
	Total: number
	Mailboxes: string[]
	DeferralReasons: string[]
}

class API {
	customer: CustomerAPI
	account: AccountAPI
	paymentPlan: PaymentPlan
	cases: CasesAPI
	clients: ClientsAPI
	inbox: InboxAPI
	devTools: DevToolsAPI
	url: (path: string) => string

	constructor() {
		this.customer = new CustomerAPI()
		this.account = new AccountAPI()
		this.paymentPlan = new PaymentPlan()
		this.cases = new CasesAPI()
		this.clients = new ClientsAPI()
		this.inbox = new InboxAPI()
		this.devTools = new DevToolsAPI()
		this.url = (path) => apiBaseUrl() + path
	}
}

function RelativeURL(path: string) {
	const defaultBase = "https://path"
	const url = new URL(path, defaultBase)

	function appendQueryParam(name: string, value: string) {
		const newURL = new URL(url)
		newURL.searchParams.append(name, value)

		return RelativeURL(newURL.toString().replace(defaultBase, ""))
	}

	function toString() {
		return url.toString().replace(defaultBase, "")
	}

	return {appendQueryParam, toString}
}

// buildAdvancedSearchQuery serializes advanced-search params into the
// `/customers/search` path + query string. Empty/whitespace-only string fields
// are dropped (so we never send `lastName=`), and the historic-contacts flag is
// only appended when enabled. Kept pure (and exported) so it can be unit-tested.
function buildAdvancedSearchQuery(params: AdvancedSearchParams): string {
	let url = RelativeURL("/customers/search")

	const stringParams: [string, string | undefined][] = [
		["clientId", params.clientID],
		["ssnLast4", params.ssnLast4],
		["firstName", params.firstName],
		["middleName", params.middleName],
		["lastName", params.lastName],
		["phone", params.phone],
		["email", params.email],
		["addressLine1", params.addressLine1],
		["postcode", params.postcode],
		["customerAccountRef", params.customerAccountRef],
		["dob", params.dob],
		["customFieldValue", params.customFieldValue],
		["customFieldName", params.customFieldName],
	]

	for (const [name, value] of stringParams) {
		const trimmed = value?.trim()
		if (trimmed) {
			url = url.appendQueryParam(name, trimmed)
		}
	}

	if (params.includeHistoricContactDetails) {
		url = url.appendQueryParam("includeHistoricContactDetails", "true")
	}

	if (params.limit !== undefined) {
		url = url.appendQueryParam("limit", String(params.limit))
	}

	return url.toString()
}

class CustomerAPI {
	search(query: string): Promise<CustomerSearchResponse> {
		return get(`/customers?q=${encodeURIComponent(query)}`)
	}

	advancedSearch(params: AdvancedSearchParams): Promise<CustomerSearchResponse> {
		return get(buildAdvancedSearchQuery(params))
	}

	get(id: string, viewerTimezoneOffset: number | string): Promise<Customer> {
		return get(`/customers/${id}?ViewerTimezoneOffsetInMinutes=${viewerTimezoneOffset}`)
	}

	releaseCollectionHold(
		id: string,
		holdID: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/collection-holds/${holdID}/release`, payload)
	}

	addEmail(
		id: string,
		payload: {
			Email: string | undefined
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/add-email`, payload)
	}

	changeAddress(
		id: string,
		payload: {
			Line1: string | undefined
			Line2: string | undefined
			City: string | undefined
			State: string | undefined
			Postcode: string | undefined
			Country: string | undefined
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/change-address`, payload)
	}

	removeEmail(
		id: string,
		payload: {
			Email: string
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/remove-email`, payload)
	}

	removeEmailLead(
		id: string,
		payload: {
			EmailLead: string
			AgentEmail: string | undefined
			Reason: string
		},
	) {
		return post(`/customers/${id}/remove-email-lead`, payload)
	}

	addPhone(
		id: string,
		payload: {
			Phone: string | undefined
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/add-phone`, payload)
	}

	removePhone(
		id: string,
		payload: {
			Phone: string
			AgentEmail: string | undefined
		},
	) {
		return post(`/customers/${id}/remove-phone`, payload)
	}

	changeComms(
		id: string,
		{actionID, channel, ...payload}: {actionID: string; channel: string; AgentEmail: string | undefined},
	) {
		return post(`/customers/${id}/${actionID}/${channel}`, payload)
	}

	changeDigitalCommConsent(id: string, {actionID, ...payload}: {actionID: string; AgentEmail: string | undefined}) {
		return post(`/customers/${id}/digital-communication-consent/${actionID}`, payload)
	}

	changeEmailConsent(
		id: string,
		{actionID, ...payload}: {actionID: string; Email: string; AgentEmail: string | undefined},
	) {
		return post(`/customers/${id}/email-consent/${actionID}`, payload)
	}

	changePhoneConsent(
		id: string,
		{actionID, ...payload}: {actionID: string; Phone: string; AgentEmail: string | undefined},
	) {
		return post(`/customers/${id}/phone-consent/${actionID}`, payload)
	}

	addNote(
		customerID: string,
		payload: {
			Body: string
			AgentEmail: string | undefined
			AgentName: string | undefined
		},
	) {
		return post(`/customers/${customerID}/add-note`, payload)
	}

	generatePaymentLink(
		customerID: string,
		payload: {
			AgentID: string | undefined
		},
	): Promise<{Message: string; PortalURL?: string}> {
		return post(`/customers/${customerID}/payment-link`, payload)
	}

	activities(customerID: string): Promise<CustomerActivitiesResponse> {
		return get(`/customers/${customerID}/activities`)
	}

	downloadEmailPDFURL(customerID: string, activityID: string) {
		return apiBaseUrl() + `/customers/${customerID}/activities/email/${activityID}/pdf`
	}

	settlementOfferCalculate(
		customerID: string,
		payload: {
			CampaignEndDate: string
			Discount: string | undefined
			AccountIDs: string[]
		},
	) {
		return post(`/customers/${customerID}/settlement-offer/calculate`, payload)
	}

	getSettlementOffer(customerID: string): Promise<SettlementOfferResponse> {
		return get(`/customers/${customerID}/settlement-offer`)
	}

	getEligibleOffers(customerID: string): Promise<OfferData> {
		return get(`/customers/${customerID}/eligible-offers`)
	}

	createOffer(
		customerID: string,
		payload: {
			offerId: string
			startDate: string
			endDate: string
			tiers: Array<
				| {tierId: string; tierType: "ONE_OFF"; discountRate: number}
				| {tierId: string; tierType: "PLAN"; discountRate: number; timeFrameWeeks: number}
			>
			agentEmail?: string
			notes?: string
		},
	) {
		return post(`/customers/${customerID}/offers`, payload)
	}

	getOffers(customerID: string): Promise<CustomerOffersResponse> {
		return get(`/customers/${customerID}/offers`)
	}

	revokeOffer(
		customerID: string,
		offerID: string,
		payload: {
			Reason: string
			AgentEmail: string | undefined
			Notes?: string
		},
	) {
		return post(`/customers/${customerID}/offers/${offerID}/revoke`, payload)
	}

	requestESIGNConsent(customerID: string, agentEmail: string | undefined, to?: string) {
		return post(`/customers/${customerID}/request-esign-consent`, {
			AgentEmail: agentEmail,
			To: to,
		})
	}

	revokeESIGNConsent(customerID: string, revokedAt: string, revokedVia: string, agentEmail: string) {
		return post(`/customers/${customerID}/revoke-esign-consent`, {
			RevokedAt: revokedAt,
			RevokedVia: revokedVia,
			AgentEmail: agentEmail,
		})
	}

	getInstalmentCaps(customerID: string): Promise<InstalmentCapsResponse> {
		return get(`/customers/${customerID}/instalmentcaps`)
	}

	extendInstalmentCap(customerID: string, maxPayments: number, agentEmail: string | undefined): Promise<void> {
		return post(`/customers/${customerID}/instalmentcaps`, {
			WeeklyMaxDurationOverride: maxPayments,
			AgentEmail: agentEmail,
		})
	}

	resetInstalmentCap(customerID: string, agentEmail: string | undefined): Promise<void> {
		return del(`/customers/${customerID}/instalmentcaps`, {
			AgentEmail: agentEmail,
		})
	}

	getNotes(customerID: string): Promise<{
		Forms: Array<{
			Type: string
			Label: string
			Version: number
			Fields: Array<{Name: string; Type: string; Required: boolean; Options?: string[]}>
		}>
		Notes: Array<{
			ID: string
			CustomerID: string
			Form: {
				Type: string
				Version: number
				Fields: Array<{Name: string; Type: string; Content: string | number | boolean}>
			}
			Status: {ID: string; Label: string}
			Uploads: Array<{ID: string; FileName: string}>
			AgentName: string
			AgentEmail: string
			CreatedAt: string
		}>
	}> {
		return get(`/customers/${customerID}/notes`)
	}

	createNote(
		customerID: string,
		payload: {
			FormType: string
			FormVersion: number
			Answers: Array<{Name: string; Type: string; Content: string | number | boolean}>
			Uploads: Array<{ID: string; FileName: string}>
			AgentName: string | undefined
			AgentEmail: string | undefined
			Pending?: boolean
		},
	): Promise<{NoteID: string}> {
		return post(`/customers/${customerID}/notes`, payload)
	}

	getUploadURL(customerID: string): Promise<{UploadID: string; SignedURL: string}> {
		return post(`/customers/${customerID}/uploads`)
	}

	getUploadContentURL(customerID: string, uploadID: string): string {
		return apiBaseUrl() + `/customers/${customerID}/uploads/${uploadID}/content`
	}

	getNoteUploadContentURL(customerID: string, noteID: string, uploadID: string): string {
		return apiBaseUrl() + `/customers/${customerID}/notes/${noteID}/uploads/${uploadID}/content`
	}

	// Case Management
	getCases(customerID: string, agentEmail: string | undefined): Promise<CasesResponse> {
		let url = RelativeURL(`/customers/${customerID}/cases`)
		if (agentEmail) url = url.appendQueryParam("AgentEmail", agentEmail)
		return get(url.toString())
	}

	scaffoldCase(customerID: string, caseType: string): Promise<ScaffoldCaseResponse> {
		return post(`/customers/${customerID}/cases?caseType=${encodeURIComponent(caseType)}`)
	}

	submitCase(
		customerID: string,
		caseID: string,
		payload: {
			CaseType: string
			AccountIDs?: string[]
			NoteID: string
			ExpiryDuration?: string
			AgentName: string | undefined
			AgentEmail: string | undefined
		},
	): Promise<void> {
		return put(`/customers/${customerID}/cases/${caseID}`, payload)
	}

	resolveCase(
		customerID: string,
		caseID: string,
		payload: {
			Resolution: string
			Reason?: string
			AccountIDs?: string[]
			NoteID?: string
			AgentName: string | undefined
			AgentEmail: string | undefined
		},
	): Promise<void> {
		return post(`/customers/${customerID}/cases/${caseID}/resolve`, payload)
	}

	parkCase(
		customerID: string,
		caseID: string,
		payload: {
			WaitParty: string
			WaitReason?: string
			ChosenWaitUntil?: string
			AgentName: string | undefined
			AgentEmail: string | undefined
		},
	): Promise<void> {
		return post(`/customers/${customerID}/cases/${caseID}/park`, payload)
	}

	returnCase(
		customerID: string,
		caseID: string,
		payload: {
			WaitParty: string
			AgentName: string
			AgentEmail: string
		},
	): Promise<void> {
		return post(`/customers/${customerID}/cases/${caseID}/return`, payload)
	}

	unassignCase(
		customerID: string,
		caseID: string,
		payload: {
			AgentEmail: string
			AgentName: string
		},
	): Promise<void> {
		return post(`/customers/${customerID}/cases/${caseID}/unassign`, payload)
	}

	pickUpCase(
		customerID: string,
		caseID: string,
		payload: {
			AgentEmail: string
			AgentName: string
		},
	): Promise<void> {
		return post(`/customers/${customerID}/cases/${caseID}/pick-up`, payload)
	}

	raiseCaseJiraTicket(
		customerID: string,
		caseID: string,
		payload: {
			AgentName: string
			AgentEmail: string
		},
	): Promise<CaseJiraTicket> {
		return post(`/customers/${customerID}/cases/${caseID}/jira-ticket`, payload)
	}

	getCaseNotes(customerID: string, caseID: string): Promise<CaseNote[]> {
		return get(`/customers/${customerID}/cases/${caseID}/notes`)
	}

	attachCaseNote(customerID: string, caseID: string, noteID: string): Promise<void> {
		return post(`/customers/${customerID}/cases/${caseID}/notes`, {NoteID: noteID})
	}

	// Conversations
	getCustomerConversations(customerID: string): Promise<ConversationSummary[]> {
		return get(`/customers/${customerID}/conversations`)
	}

	getCaseConversations(customerID: string, caseID: string): Promise<CaseConversationSummary[]> {
		return get(`/customers/${customerID}/cases/${caseID}/conversations`)
	}

	getConversation(
		customerID: string,
		conversationID: string,
		{limit, offset, agentEmail}: {limit: number; offset: number; agentEmail?: string},
	): Promise<ConversationTimeline> {
		let url = RelativeURL(`/customers/${customerID}/conversations/${conversationID}`)
			.appendQueryParam("limit", String(limit))
			.appendQueryParam("offset", String(offset))
		if (agentEmail) url = url.appendQueryParam("AgentEmail", agentEmail)
		return get(url.toString())
	}

	getConversationMessageAttachmentURL(
		customerID: string,
		conversationID: string,
		messageID: string,
		index: number,
	): string {
		return (
			apiBaseUrl() +
			`/customers/${customerID}/conversations/${conversationID}/messages/${messageID}/attachments/${index}`
		)
	}

	draftReply(
		customerID: string,
		conversationID: string,
		providerMessageId: string,
		payload: DraftReplyRequest,
	): Promise<DraftReplyResponse> {
		return post(
			`/customers/${customerID}/conversations/${conversationID}/messages/${providerMessageId}/draft-reply`,
			payload,
		)
	}

	draftSMSReply(
		customerID: string,
		conversationID: string,
		providerMessageId: string,
		payload: DraftReplyRequest,
	): Promise<SMSDraftReplyResponse> {
		return post(
			`/customers/${customerID}/conversations/${conversationID}/messages/${providerMessageId}/draft-sms-reply`,
			payload,
		)
	}

	sendSMSReply(
		customerID: string,
		conversationID: string,
		providerMessageId: string,
		payload: SendSMSReplyRequest,
	): Promise<SendSMSReplyResponse> {
		return post(
			`/customers/${customerID}/conversations/${conversationID}/messages/${providerMessageId}/send-sms-reply`,
			payload,
		)
	}

	sendReply(
		customerID: string,
		conversationID: string,
		providerMessageId: string,
		payload: SendReplyRequest,
	): Promise<SendReplyResponse> {
		return post(
			`/customers/${customerID}/conversations/${conversationID}/messages/${providerMessageId}/send-reply`,
			payload,
		)
	}

	archiveNote(
		customerID: string,
		noteID: string,
		payload: {
			Status: string
		},
	): Promise<void> {
		return post(`/customers/${customerID}/notes/${noteID}/archive`, payload)
	}
}

class AccountAPI {
	dispute(
		id: string,
		dispute: {
			Reason: string
			AgentEmail: string | undefined
		},
	) {
		return post(`/accounts/${id}/dispute`, dispute)
	}

	acknowledgeNotificationOfAssignment(
		id: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/accounts/${id}/acknowledge-notification-of-assignment`, payload)
	}

	reTriggerNotificationOfAssignment(
		id: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/accounts/${id}/re-trigger-notification-of-assignment`, payload)
	}

	recall(
		id: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/accounts/${id}/recall`, payload)
	}

	isReopenAfterRecalledEnabled() {
		return get("/accounts/reopen-after-recalled/enabled")
	}

	reopenAfterRecalled(
		id: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/accounts/${id}/reopen-after-recalled`, payload)
	}

	activities(id: string): Promise<AccountActivitiesResponse> {
		return get(`/accounts/${id}/activities`)
	}
}

class PaymentPlan {
	cancel(
		id: string,
		payload: {
			AgentEmail: string | undefined
		},
	) {
		return post(`/payment-plans/${id}/cancel`, payload)
	}
}

class CasesAPI {
	list(
		params: {
			country?: string
			clientID?: string
			caseTypes?: string[]
			sortType?: string
			waitParty?: string
			openedBy?: string
			openedWithin?: string
			states?: string[]
			agentEmail?: string
		} = {},
	): Promise<ClientCasesResponse> {
		let url = RelativeURL("/cases")
		if (params.country) url = url.appendQueryParam("country", params.country)
		if (params.clientID) url = url.appendQueryParam("clientID", params.clientID)
		if (params.caseTypes?.length) url = url.appendQueryParam("caseType", params.caseTypes.join(","))
		if (params.sortType) url = url.appendQueryParam("sortType", params.sortType)
		if (params.waitParty) url = url.appendQueryParam("waitParty", params.waitParty)
		if (params.openedBy) url = url.appendQueryParam("openedBy", params.openedBy)
		if (params.openedWithin) url = url.appendQueryParam("openedWithin", params.openedWithin)
		if (params.states?.length) url = url.appendQueryParam("state", params.states.join(","))
		if (params.agentEmail) url = url.appendQueryParam("AgentEmail", params.agentEmail)
		return get(url.toString())
	}

	export(payload: CasesExportRequest): Promise<CasesExportSubmitResponse> {
		return post("/cases/export", payload)
	}

	pickUp(payload: {
		AgentEmail: string
		AgentName: string
		Country?: string
		ClientID?: string
		CaseTypes: string[]
		SortType?: string
		OpenedBy?: string
		OpenedWithin?: string
		States?: string[]
	}): Promise<CasesPickUpResponse> {
		return post("/cases/pick-up", payload)
	}
}

class ClientsAPI {
	list(): Promise<ClientsListResponse> {
		return get("/clients")
	}
}

class InboxAPI {
	listUnidentified(
		// from is an RFC 3339 instant rather than a date: the browser picking it
		// is the only party that knows which midnight the agent meant.
		params: {
			limit?: number
			offset?: number
			mailbox?: string
			reason?: string
			from?: string
			conversation?: string
		} = {},
	): Promise<UnidentifiedInboxResponse> {
		let url = RelativeURL("/inbox/unidentified")
		if (params.limit) url = url.appendQueryParam("limit", String(params.limit))
		if (params.offset) url = url.appendQueryParam("offset", String(params.offset))
		if (params.mailbox) url = url.appendQueryParam("mailbox", params.mailbox)
		if (params.reason) url = url.appendQueryParam("reason", params.reason)
		if (params.from) url = url.appendQueryParam("from", params.from)
		if (params.conversation) url = url.appendQueryParam("conversation", params.conversation)
		return get(url.toString())
	}

	// The reader builds its own request rather than going through req(), which
	// drops the status it needs, so it takes the path from here to keep one owner.
	unidentifiedItemPath(channel: string, conversationID: string, providerMessageID: string): string {
		return (
			`/inbox/unidentified/${encodeURIComponent(channel)}/${encodeURIComponent(conversationID)}` +
			`/messages/${encodeURIComponent(providerMessageID)}`
		)
	}

	dismissUnidentified(
		channel: string,
		conversationID: string,
		providerMessageID: string,
		payload: {Reason: string; Note: string; HandedOff: boolean; AgentEmail: string | undefined},
	): Promise<void> {
		return post(`${this.unidentifiedItemPath(channel, conversationID, providerMessageID)}/dismiss`, payload)
	}
}

class DevToolsAPI {
	listClients(): Promise<DevToolsClient[]> {
		return get("/dev/clients")
	}

	createCustomer(payload: DevToolsCreateCustomerRequest): Promise<DevToolsCreateCustomerResponse> {
		return post("/dev/customers", payload)
	}
}

function get(endpoint: string, options = {}) {
	return req(endpoint, {...options, method: "GET"})
}

function post(endpoint: string, body: any = undefined, options = {}) {
	return req(endpoint, {...options, method: "POST", body: JSON.stringify(body)})
}

function put(endpoint: string, body: any = undefined, options = {}) {
	return req(endpoint, {...options, method: "PUT", body: JSON.stringify(body)})
}

function del(endpoint: string, body: any = undefined, options = {}) {
	return req(endpoint, {...options, method: "DELETE", body: JSON.stringify(body)})
}

function req(endpoint: string, options: RequestInit) {
	return new Promise<any>((resolve, reject) => {
		fetch(apiBaseUrl() + endpoint, options)
			.then((res) => {
				const contentType = res.headers.get("Content-Type")
				const isJSON = contentType && contentType.includes("application/json")
				const promise = isJSON ? res.json() : res.text()
				promise.then((body) => (res.status >= 400 ? reject(apiError(body)) : resolve(body)))
			})
			.catch(() => {
				reject(apiError("Network error"))
			})
	})
}

function apiBaseUrl() {
	return import.meta.env.VITE_API_URL
}

function apiError(error: unknown) {
	const unknownError = {Message: "Unknown error"}
	if (!error) return unknownError
	if (typeof error == "string") return {Message: error}
	if (typeof error === "object") {
		if ("message" in error && typeof error.message === "string") {
			return {Message: error.message}
		}
		if ("Message" in error) return error as {Message: string}
		// A typed refusal carries the words the endpoint chose under Reason; without
		// this they flatten to "Unknown error" and the agent is told nothing.
		// Retryable travels with them: whether pressing again can land differently
		// is the endpoint's judgement to make, never one re-derived here.
		if ("Reason" in error && typeof error.Reason === "string") {
			return {Message: error.Reason, Retryable: "Retryable" in error ? error.Retryable === true : undefined}
		}
		if ("Errors" in error && typeof error.Errors === "object" && error.Errors !== null) {
			const message = Object.keys(error.Errors)
				.map((key) => {
					const errorValue = (error.Errors as Record<string, unknown>)[key]
					if (Array.isArray(errorValue)) {
						return errorValue.map((msg) => `${key} ${msg}`)
					}
					return []
				})
				.flat()
				.join(".")
			return {Message: message}
		}
	}
	return unknownError
}

const api = new API()

export {api, buildAdvancedSearchQuery}
export type {
	ClientCasesClient,
	ClientListItem,
	ClientsListResponse,
	ClientCasesCaseType,
	ClientCasesSortOption,
	ClientCasesOpenedByOption,
	ClientCasesOpenedWithinOption,
	ClientCase,
	ClientMyCase,
	ClientCasesResponse,
	ClientCasesExportType,
	ClientCasesExportColumn,
	CasesExportRequest,
	CasesExportSubmitResponse,
	Customer,
	CustomerSearchResponse,
	CustomerSearchResult,
	HistoricMatch,
	AdvancedSearchParams,
	CustomerStatus,
	ContactConsent,
	EmailWithDetails,
	PhoneWithDetails,
	LatitudeInfo,
	Account,
	PaymentPlanAccount,
	ClientBanner,
	ActivePaymentPlan,
	ActiveInstalmentCap,
	InstalmentCapHistoryEntry,
	InstalmentCapsResponse,
	OutstandingAccount,
	CustomerActivity,
	CustomerActivitiesResponse,
	AccountActivity,
	AccountActivityPayload,
	AccountActivitiesResponse,
	AccountDetail,
	OtherAccount,
	AccountStatus,
	CustomerCreatedActivityPayload,
	CustomerLatitudeReferredActivityPayload,
	CustomerLatitudeConvertedActivityPayload,
	CustomerAddressChangedActivityPayload,
	CustomerAddressChangedByCustomerActivityPayload,
	CustomerCaseEscalatedActivityPayload,
	CustomerCaseSubmittedActivityPayload,
	CustomerCaseResolvedActivityPayload,
	CustomerCaseAccountResolvedActivityPayload,
	CustomerCommunicationSnoozedActivityPayload,
	CustomerCustomField,
	CustomerCustomFieldsRecordedActivityPayload,
	CustomerDigitalCommunicationConsentActivityPayload,
	CustomerEmailCommunicationOptedActivityPayload,
	CustomerEmailLeadRemovedActivityPayload,
	CustomerEmailLeadsUpdateActivityPayload,
	CustomerEmailSentActivityPayload,
	CustomerEmailUpdateActivityPayload,
	CustomerEmailUpdateByCustomerActivityPayload,
	CustomerESIGNConsentRequestedPayload,
	CustomerESIGNConsentRevokedByAgentPayload,
	CustomerInformationUpdatedActivityPayload,
	CustomerInformationUpdatedByCustomerActivityPayload,
	CustomerInstalmentOverrideAppliedActivityPayload,
	CustomerInstalmentOverrideResetActivityPayload,
	CustomerNOALetterSentActivityPayload,
	CustomerPaymentArrangementCancelled,
	CustomerPaymentArrangementCompleted,
	CustomerPaymentArrangementSetupSucceeded,
	CustomerPaymentPlanCancelled,
	CustomerPaymentPlanCompleted,
	CustomerPaymentPlanSetupSucceeded,
	CustomerPhoneIdentityMatchActivityPayload,
	CustomerPhoneOptedOutForSMSActivityPayload,
	CustomerPhoneReassignedNumberActivityPayload,
	CustomerPhoneUpdateActivityPayload,
	CustomerPhoneUpdateByCustomerActivityPayload,
	CustomerPreferredLanguageConfiguredActivityPayload,
	CustomerSMSActivityPayload,
	ContactInstruction,
	Note,
	SettlementOfferResponse,
	Transaction,
	OfferData,
	OfferDetails,
	CustomerOffer,
	CustomerOfferTier,
	CustomerOffersResponse,
	RevocationReason,
	CaseType,
	CaseResolution,
	CaseResolutionReason,
	CaseNote,
	CaseNoteStatus,
	CaseNoteArchiveStatus,
	CaseNoteForm,
	CaseWaitParty,
	CaseWaitPartyEscalation,
	CaseReturnReason,
	CaseManual,
	CaseJiraTicket,
	ActiveCase,
	ActiveCaseAccount,
	CustomerAccount,
	ClosedCase,
	ResolvedCaseAccount,
	ResolvedAccountCase,
	ResolvedCases,
	CasesResponse,
	CaseConversationSummary,
	ConversationSummary,
	ConversationMessage,
	ConversationMessageAttachment,
	ConversationTimeline,
	DraftReplyRequest,
	DraftReplyResponse,
	DraftReplyOk,
	DraftReplyBlocked,
	DraftReplyDraft,
	DraftReplyPromptMessage,
	SMSDraftReplyRequest,
	SMSDraftReplyResponse,
	SMSDraftReplyOk,
	SMSDraftReplyBlocked,
	SMSDraftReplyDraft,
	SendSMSReplyRequest,
	SendSMSReplyResponse,
	SendReplyRequest,
	SendReplyResponse,
	SendReplyAccepted,
	EmailRecipient,
	ExpiryDuration,
	ScaffoldCaseResponse,
	ScaffoldCaseAccount,
	CollectionHold,
	DevToolsStatementOfWork,
	DevToolsClient,
	DevToolsCreateCustomerRequest,
	DevToolsCreateCustomerResponse,
	UnidentifiedInboxMessage,
	UnidentifiedInboxResponse,
}
